/**
 * 시연용 배치 OCR job 목록
 */
import {
  demoOcrService,
  extractJobsArrayFromListResponse,
} from '@/entities/demo/api/demo-ocr.api';
import { removeStoredBatchJobId } from '@/pages/demo/utils/demoDocumentAiBatchStorage';
import {
  collectOrderedUniqueJobIds,
  extractBulkDeleteItemsFromResponseBody,
  getBulkDeleteJobIdLimit,
  getJobIdFromDeleteResultItem,
  isDocumentAiBatchJobDeleteItemNotFound,
  isDocumentAiBatchJobDeleteItemSuccess,
  postDocumentAiBatchJobsDelete,
} from '@/pages/demo/utils/documentAiBatchJobDelete';
import { formatBatchJobDateTime } from '@/pages/demo/utils/formatBatchJobDateTime';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { useToast } from '@/shared/utils/toast';
import { Link } from '@tanstack/react-router';
import { Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

const statusBadge = (status: unknown) => {
  const s = String(status ?? '').toLowerCase();
  if (s === 'running' || s === 'progress') return <Badge variant="secondary">{String(status)}</Badge>;
  if (s === 'succeeded' || s === 'success' || s === 'completed')
    return <Badge>{String(status)}</Badge>;
  if (s === 'failed' || s === 'error' || s === 'cancelled' || s === 'canceled')
    return <Badge variant="destructive">{String(status)}</Badge>;
  return <Badge variant="outline">{String(status ?? '—')}</Badge>;
};

export const DemoBatchJobsTable = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [listResult, setListResult] = useState<{
    ok: boolean;
    status: number | null;
    body: unknown;
    fetchedAt: Date;
  } | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await demoOcrService.fetchBatchJobs();
      setListResult(result);
      setSelected([]);
      if (!result.ok) toast.error(`배치 job 목록 요청 실패 (${result.status})`);
    } catch {
      toast.error('배치 job 목록 요청 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchJobs();
  }, [fetchJobs]);

  const rows = useMemo(() => {
    if (!listResult?.ok) return [];
    return extractJobsArrayFromListResponse(listResult.body).map((row, idx) => ({
      key: String(row?.jobId ?? row?.id ?? idx),
      status: row?.status,
      createdAt: row?.createdAt,
      lastUpdatedAt: row?.lastUpdatedAt,
      updatedAt: row?.updatedAt,
    }));
  }, [listResult]);

  const toggleRow = (key: string, checked: boolean) => {
    setSelected((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)));
  };

  const handleDelete = async () => {
    const jobIds = collectOrderedUniqueJobIds(selected);
    if (!jobIds.length) return;
    const limit = getBulkDeleteJobIdLimit();
    if (jobIds.length > limit) {
      toast.error(`한 번에 최대 ${limit}개까지 삭제할 수 있습니다.`);
      return;
    }
    setDeleting(true);
    try {
      const { ok, body } = await postDocumentAiBatchJobsDelete(jobIds);
      const items = extractBulkDeleteItemsFromResponseBody(body);
      let success = 0;
      let notFound = 0;
      let failed = 0;
      for (const item of items as Record<string, unknown>[]) {
        const id = getJobIdFromDeleteResultItem(item);
        if (isDocumentAiBatchJobDeleteItemSuccess(item)) {
          success += 1;
          if (id) removeStoredBatchJobId(id);
        } else if (isDocumentAiBatchJobDeleteItemNotFound(item)) {
          notFound += 1;
          if (id) removeStoredBatchJobId(id);
        } else {
          failed += 1;
        }
      }
      toast.success(`삭제 완료: 성공 ${success}, 없음 ${notFound}, 실패 ${failed}`);
      if (!ok && !items.length) toast.error('배치 job 삭제 요청이 실패했습니다.');
      await fetchJobs();
    } catch {
      toast.error('배치 job 삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">배치 OCR Job 목록</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void fetchJobs()} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={!selected.length || deleting}
            onClick={() => void handleDelete()}
          >
            {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
            선택 삭제
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>Job ID</TableHead>
              <TableHead>상태</TableHead>
              <TableHead>생성</TableHead>
              <TableHead>갱신</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  {loading ? '불러오는 중…' : '배치 job이 없습니다.'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const key = String(row.key);
                return (
                  <TableRow key={key}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selected.includes(key)}
                        onChange={(e) => toggleRow(key, e.target.checked)}
                        aria-label={`${key} 선택`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{key}</TableCell>
                    <TableCell>{statusBadge(row.status)}</TableCell>
                    <TableCell>{formatBatchJobDateTime(row.createdAt)}</TableCell>
                    <TableCell>{formatBatchJobDateTime(row.lastUpdatedAt ?? row.updatedAt)}</TableCell>
                    <TableCell>
                      <Button variant="link" size="sm" asChild>
                        <Link
                          to={ROUTE_PATHS.DEMO_BATCH_JOB_DETAIL as never}
                          search={{ jobId: key } as never}
                        >
                          상세
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
